import { useMemo, useRef, useState, useEffect } from 'react';
import { Checklist, DisplayItem } from '#/types/message';
import ContentCarousel from '../content-carousel/content-carousel.tsx';
import { MarkdownRenderer } from "../markdown-renderer/MarkdownRenderer.tsx";
import { MetadataBubble } from "../ai-message/metadata-bubble/MetadataBubble.tsx";
import { AlertPopup, Backdrop } from '../ai-message/alert-popup/AlertPopup.tsx';
import DeleteIcon from '#/assets/close_valtra.svg?react';
import HideIcon from '#/assets/hide_valtra.svg?react';
import ChecklistIcon from '#/assets/checklist1.svg?react'
import CheckedIcon from '#/assets/check_box_checked.svg?react';
import UncheckedIcon from '#/assets/check_box_outline_blank.svg?react';
import { useTranslation } from 'react-i18next';
import styles from './checklist-panel.module.css';

import { ChecklistState, useChecklist } from '#/contexts/ChecklistContext.tsx';


interface CheckListContentCarouselProps {
  checklist: Checklist;
  currentStepIndex: number;
  openModal: (item: DisplayItem, index: number, allItemsFromCarousel: DisplayItem[], imageListId: string) => void;
}

const CheckListContentCarousel = ({ checklist, currentStepIndex, openModal }: CheckListContentCarouselProps) => {
  const isMobileView = window.innerWidth < 851;
  const combinedItems = useMemo(() => {
    const images = checklist.steps[currentStepIndex].display_items || [];
    return images;
  }, [currentStepIndex]);

  return (
    <ContentCarousel
      key={`step-${currentStepIndex}`}
      items={combinedItems}
      onItemClick={(item, itemIndex, allItemsFromCarousel) => {
        openModal(item, itemIndex, allItemsFromCarousel, `checklist-step-${checklist.steps[currentStepIndex].order}`);
      }}
      analyzingMetadata={false}
      verticalLayout={isMobileView ? 'top' : 'bottom'}
    />
  );
};

interface ChecklistPanelProps {
  onImageClick: (item: DisplayItem, index: number, allItemsFromCarousel: DisplayItem[], groupId: string) => void;
  onRetry: (checklistId: string) => void;
}

export function ChecklistPanel({
  onImageClick,
  onRetry
}: ChecklistPanelProps) {
  const { t } = useTranslation();
  const [checkedSteps, setCheckedSteps] = useState<number[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [isAlertPopupOpen, setIsAlertPopupOpen] = useState(false);
  const currentStepIndexRef = useRef(0);
  const {
    setChecklistState,
    checklistState,
    checklistId,
    getCachedChecklist,
    isLoadingChecklist,
    checklistError
  } = useChecklist();

  const checklist = getCachedChecklist(checklistId);

  function HideButton({ onClick }: { onClick: () => void }) {
    return (
      <button className={styles.hideButton} onClick={onClick}>
        <HideIcon aria-label="hide" className={styles.hideIcon} />
        <span>{t("minimize")}</span>
      </button>
    );
  }

  useEffect(() => {
    currentStepIndexRef.current = 0;
    setCheckedSteps([]);
  }, [checklistId]);

  const handleStepToggle = (stepIndex: number) => {
    setCheckedSteps(prev => {
      const isAlreadyChecked = prev.includes(stepIndex);
      const newCheckedSteps = isAlreadyChecked
        ? prev.filter(i => i !== stepIndex)
        : [...prev, stepIndex];

      if (!isAlreadyChecked && stepIndex < (checklist?.steps.length || 0) - 1) {
        handleNext();
      }

      return newCheckedSteps;
    });
  };

  const handleStepChange = (newIndex: number, dir: 'next' | 'prev') => {
    setDirection(dir);
    setIsTransitioning(true);
    setTimeout(() => {
      currentStepIndexRef.current = newIndex;
      setIsTransitioning(false);
    }, 200);
  };

  const handleNext = () => {
    if (checklist && currentStepIndexRef.current < checklist.steps.length - 1 && !isTransitioning) {
      handleStepChange(currentStepIndexRef.current + 1, 'next');
    }
  };

  const handlePrevious = () => {
    if (currentStepIndexRef.current > 0 && !isTransitioning) {
      handleStepChange(currentStepIndexRef.current - 1, 'prev');
    }
  };

  const handleHide = (hide: boolean) => {
    setChecklistState(hide ? ChecklistState.HIDDEN : ChecklistState.OPEN);
  };

  const handleAlertsClick = () => {
    if (checklist?.steps[currentStepIndexRef.current] && checklist.steps[currentStepIndexRef.current].alerts) {
      setIsAlertPopupOpen(true);
    }
  };

  const displayItemsForCarousel: DisplayItem[] = checklist?.steps[currentStepIndexRef.current].display_items || [];

  const currentStepIndex = currentStepIndexRef.current;
  const isChecked = checkedSteps.includes(currentStepIndex);

  return (
    <>
      <div
        className={`${styles.checklistPanel} ${checklistState === ChecklistState.OPEN ? styles.open : ''} ${checklistState === ChecklistState.HIDDEN ? styles.hidden : ''}`}
      >
        <div className={styles.header}>
          {checklist && (
            <div className={styles.checklistTitle}>{checklist.title}</div>
          )}

          <div className={styles.headerButtons}>
            <HideButton onClick={() => handleHide(true)} />
            <button
              className={styles.closeButton}
              onClick={() => setChecklistState(ChecklistState.CLOSED)}
            >
              <DeleteIcon className={styles.deleteIcon} aria-label="delete" />
              <span>{t("close")}</span>
            </button>
          </div>
        </div>

        <div className={styles.stepsContainer}>
          {isLoadingChecklist && (
            <div className={styles.loadingState}>
              <p>{t("loadingChecklist")}</p>
            </div>
          )}

          {checklistError && (
            <div className={styles.errorState}>
              <p>{checklistError}</p>
              <button onClick={() => onRetry(checklistId)}>{t("tryAgain")}</button>
            </div>
          )}

          {checklist && !isLoadingChecklist && !checklistError && (
            <>

              {checklist && (
                <div className={styles.stepCounter}>
                  {t("step")} {currentStepIndexRef.current + 1} {t("of")} {checklist.steps.length}
                </div>
              )}

              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${(currentStepIndexRef.current + 1) / checklist.steps.length * 100}%` }}
                />
              </div>

              <div className={styles.stepContent}>
                {checklist.steps[currentStepIndexRef.current] && (
                  <div className={`${styles.currentStep} ${isTransitioning
                    ? direction === 'next'
                      ? styles.slideOutLeft
                      : styles.slideOutRight
                    : direction === 'next'
                      ? styles.slideInRight
                      : styles.slideInLeft
                    }`}>

                    {/* Inline alerts */}
                    {checklist.steps[currentStepIndexRef.current].alerts &&
                    checklist.steps[currentStepIndexRef.current].alerts!.length > 0 && (
                      <div className={styles.inlineAlerts}>
                        <AlertPopup
                          alerts={checklist.steps[currentStepIndexRef.current].alerts || []}
                          onClose={() => {}}
                          inline={true}
                        />
                      </div>
                    )}

                    {/* Step text and images */}
                    <div className={styles.stepDetails}>
                      <MarkdownRenderer markdown={checklist.steps[currentStepIndexRef.current].text} />
                      {displayItemsForCarousel.length > 0 && (
                        <CheckListContentCarousel
                          checklist={checklist}
                          currentStepIndex={currentStepIndexRef.current}
                          openModal={onImageClick}
                        />
                      )}
                    </div>

                    {/* Keep links in MetadataBubble if needed */}
                    {checklist.steps[currentStepIndexRef.current].links &&
                    checklist.steps[currentStepIndexRef.current].links!.length > 0 && (
                      <MetadataBubble
                        alerts={[]}
                        onAlertClick={() => {
                          handleAlertsClick();
                        }}
                        links={checklist.steps[currentStepIndexRef.current].links || []}
                        onLinkClick={(url) => window.open(url, '_blank')}
                        margin={false}
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className={styles.navigationButtons}>
          {/* Previous */}
          <button
            onClick={handlePrevious}
            disabled={currentStepIndexRef.current === 0}
            className={`${styles.navBtn} ${styles.navSecondary}`}
            aria-label={t("previousStep")}
          >
            <svg className={styles.icon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.825 9L9.425 14.6L8 16L0 8L8 0L9.425 1.4L3.825 7H16V9H3.825Z" fill="currentColor"/>
            </svg>
            <span>{t("previousStep") || "Previous step"}</span>
          </button>

          {/* Mark as done */}
          {checklist && (
            <button
              onClick={() => {
                  handleStepToggle(currentStepIndex);
              }}
              className={`${styles.navBtn} ${styles.navPrimary} ${isChecked ? styles.isDone : ""}`}
              aria-pressed={isChecked}
            >
              <span className={styles.iconBox}>
                {isChecked ? (
                  <CheckedIcon className={styles.iconImg} aria-hidden="true" />
                ) : (
                  <UncheckedIcon className={styles.iconImg} aria-hidden="true" />
                )}
              </span>
              <span>
                {(t("markAsDone")   || "Mark as done")}
              </span>
            </button>
          )}

          {/* Next */}
          <button
            onClick={handleNext}
            disabled={checklist ? currentStepIndexRef.current === checklist.steps.length - 1 : true}
            className={`${styles.navBtn} ${styles.navSecondary}`}
            aria-label={t("nextStep")}
          >
            <span>{t("nextStep") || "Next step"}</span>
            <svg className={styles.icon} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M12.175 9H0V7H12.175L6.575 1.4L8 0L16 8L8 16L6.575 14.6L12.175 9Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      {isAlertPopupOpen && checklist && (
        <>
          <Backdrop
            isVisible={isAlertPopupOpen}
            onClick={() => setIsAlertPopupOpen(false)}
          />
          <AlertPopup
            alerts={checklist.steps[currentStepIndexRef.current].alerts || []}
            onClose={() => setIsAlertPopupOpen(false)}
          />
        </>
      )}

      {checklistState === ChecklistState.HIDDEN && (
        <button
          className={styles.checklistPeek}
          onClick={() => handleHide(false)}
          title="Show Checklist"
        >
          <div className={styles.peekContent}>
            <ChecklistIcon aria-label="checklist" />
          </div>
        </button>
      )}
    </>
  );
}